<script setup lang="ts">
import UIInput from '@/shared/ui/Input/Input.vue';

const props = defineProps<{
  minPrice: number;
  maxPrice: number;
  defaultMinPrice: number;
  defaultMaxPrice: number;
}>();

const emit = defineEmits<{
  (e: 'update:minPrice', value: number): void;
  (e: 'update:maxPrice', value: number): void;
}>();

const { t } = useI18n();

const priceInput = ref<{ from: string | number; to: string | number }>({
  from: props.minPrice,
  to: props.maxPrice,
});

const priceSlider = ref({
  from: props.minPrice,
  to: props.maxPrice,
});

const isSliderSelected: Ref<'from' | 'to' | null> = ref(null);
const sliderRef: Ref<HTMLElement | null> = ref(null);

const updateSliderStyles = (sliderElement: HTMLElement | null): void => {
  if (!sliderElement) return;

  const range = props.defaultMaxPrice - props.defaultMinPrice;
  const fromPercent =
    ((priceSlider.value.from - props.defaultMinPrice) / range) * 100 + '%';
  const toPercent = ((priceSlider.value.to - props.defaultMinPrice) / range) * 100 + '%';

  sliderElement.style.setProperty('--from-percent', fromPercent);
  sliderElement.style.setProperty('--to-percent', toPercent);
};

const handleSliderMouseDown = (slider: 'from' | 'to'): void => {
  isSliderSelected.value = slider;
};

onMounted(() => {
  updateSliderStyles(sliderRef.value);
});

watch(
  () => props.minPrice,
  newValue => {
    priceInput.value.from = newValue;
    priceSlider.value.from = newValue;
    updateSliderStyles(sliderRef.value);
  },
  { immediate: true },
);

watch(
  () => props.maxPrice,
  newValue => {
    priceInput.value.to = newValue;
    priceSlider.value.to = newValue;
    updateSliderStyles(sliderRef.value);
  },
  { immediate: true },
);

watch(
  [() => priceInput.value.from, () => priceInput.value.to],
  ([newFrom, newTo], [oldFrom]) => {
    const fromValue = Number(newFrom) || props.defaultMinPrice;
    const toValue = Number(newTo) || props.defaultMaxPrice;
    const direction = newFrom !== oldFrom ? 'from' : 'to';

    if (toValue > props.defaultMaxPrice) {
      priceInput.value.to = props.defaultMaxPrice.toString();
      priceSlider.value.to = props.defaultMaxPrice;
      return;
    }

    priceSlider.value = {
      ...priceSlider.value,
      [direction]: direction === 'from' ? fromValue : toValue,
    };
    updateSliderStyles(sliderRef.value);
    emit('update:minPrice', Number(priceSlider.value.from));
    emit('update:maxPrice', Number(priceSlider.value.to));
  },
);

watch(
  [() => priceSlider.value.from, () => priceSlider.value.to],
  ([newFrom, newTo], [oldFrom]) => {
    const direction = newFrom !== oldFrom ? 'from' : 'to';
    priceInput.value = {
      ...priceInput.value,
      [direction]: priceSlider.value[direction].toString(),
    };
    updateSliderStyles(sliderRef.value);
    emit('update:minPrice', Number(priceSlider.value.from));
    emit('update:maxPrice', Number(priceSlider.value.to));
  },
);
</script>

<template>
  <div class="price">
    <div class="price__inputs">
      <UIInput
        v-model="priceInput.from"
        class="price__input"
        type="text"
        :label-inside-text="t('from')"
      />
      <UIInput
        v-model="priceInput.to"
        class="price__input"
        type="text"
        :label-inside-text="t('to')"
      />
    </div>
    <div class="price__slider" ref="sliderRef">
      <input
        type="range"
        :class="['price__slider-input', { _selected: isSliderSelected === 'from' }]"
        v-model="priceSlider.from"
        :min="defaultMinPrice"
        :max="defaultMaxPrice"
        :step="1000"
        @mousedown="handleSliderMouseDown('from')"
        @touchstart="handleSliderMouseDown('from')"
      />
      <input
        type="range"
        :class="['price__slider-input', { _selected: isSliderSelected === 'to' }]"
        v-model="priceSlider.to"
        :min="defaultMinPrice"
        :max="defaultMaxPrice"
        :step="1000"
        @mousedown="handleSliderMouseDown('to')"
        @touchstart="handleSliderMouseDown('to')"
      />
    </div>
  </div>
</template>

<style lang="less" scoped>
.price {
  .flex-column(@size-x4);

  &__inputs {
    display: flex;
    gap: @size-x3;
  }

  &__input {
    flex: 1;
  }

  &__slider {
    position: relative;
    height: 2px;
    background: #e0e0e0;
    border-radius: 1px;
    margin: 14px @size-x3;
    width: calc(100% - @size-x6);
  }

  &__slider-input {
    position: absolute;
    top: 50%;
    left: -@size-x3;
    right: -@size-x3;
    width: calc(100% + @size-x6);
    -webkit-appearance: none;
    pointer-events: none;
    background: none;
    transform: translateY(-50%);
    padding: 0;
    margin: 0;
    z-index: 1;

    &._selected {
      z-index: 2;
    }

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      pointer-events: auto;
      width: @size-x7;
      height: @size-x7;
      border-radius: 50%;
      background: #fff;
      cursor: pointer;
      border: 2px solid white;
      box-shadow: 0px @main-size @main-size 0px rgba(#000, 0.25);
    }

    &::-moz-range-thumb {
      width: @size-x7;
      height: @size-x7;
      border-radius: 50%;
      background: #fff;
      cursor: pointer;
      border: 2px solid #fff;
      box-shadow: 0px @main-size @main-size 0px rgba(#000, 0.25);
    }
  }

  &__slider::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: @main-size;
    background: linear-gradient(
      to right,
      @color-alto 0%,
      @color-alto var(--from-percent),
      @color-tango var(--from-percent),
      @color-tango var(--to-percent),
      @color-alto var(--to-percent)
    );
    border-radius: @size-x2;
  }
}
</style>
